import {
  Box,
  CircularProgress,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import {Brain, FileInput, X} from "lucide-react";
import {useCallback, useMemo, useState} from "react";
import toast from "react-hot-toast";
import {useDropzone} from "react-dropzone";
import {FileAnalyzeStatus, FileFragmentFragment, FileType,} from "@/graphql/graphql";
import {FILE_TYPE_COLORS} from "@/types/FileType.tsx";
import {uploadFile} from "@/graphql/api.tsx";
import {useAuth0} from "@auth0/auth0-react";

interface FileInputListItemProps {
  file?: FileFragmentFragment;
  fileType: FileType;
  title: string;
  disabled?: boolean;
  onClickDelete?: (file: FileFragmentFragment) => Promise<void>;
  onFileNameClick?: (file: FileFragmentFragment) => void;
  zyusetuId: string;
  onClickAnalyze?: (file: FileFragmentFragment[]) => Promise<void>;
  onUploaded?: (file: FileFragmentFragment) => void;
}

export function FileInputListItem({
                                    file,
                                    title,
                                    fileType,
                                    onFileNameClick,
                                    onClickAnalyze,
                                    onClickDelete,
                                    disabled,
                                    zyusetuId,
                                    onUploaded
                                  }: FileInputListItemProps) {
  const [isUploading, setIsUploading] = useState(false);
  
  const {getAccessTokenSilently} = useAuth0();
  
  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;
      
      setIsUploading(true);
      
      const loading = toast.loading("ファイルをアップロードしています");
      
      try {
        const uploadedFile = await uploadFile(
          file,
          fileType,
          await getAccessTokenSilently({
            authorizationParams: {
              audience: "ai-zyusetu"
            }
          }),
          zyusetuId,
        );
        
        if (!uploadedFile) {
          toast.error("ファイルのアップロードに失敗しました", {id: loading});
          return;
        }
        
        toast.success("ファイルアップロードに成功しました", {id: loading});
        onUploaded?.(uploadedFile);
        toast.loading("ファイルを解析しています", {id: loading});
        await onClickAnalyze?.([uploadedFile]);
        
        toast.success("ファイルの解析に成功しました", {id: loading});
        
      } catch (e) {
        toast.error("ファイルのアップロードに失敗しました", {id: loading});
      } finally {
        setIsUploading(false);
      }
    },
    [fileType, getAccessTokenSilently, onClickAnalyze, onUploaded, zyusetuId],
  );
  
  const isDisabled = useMemo(
    () => disabled || isUploading || !!file,
    [disabled, isUploading, file],
  );
  
  const {getRootProps, getInputProps} = useDropzone({
    onDrop,
    disabled: isDisabled || !onClickAnalyze,
    accept: {
      "application/pdf": [".pdf"],
      "image/*": [".png", ".jpg", ".jpeg"],
    },
    multiple: false,
  });
  
  return (
    <ListItem
      sx={{
        cursor: isDisabled ? "default" : "pointer",
        position: 'relative',
        pl: 3, // 左側にスペースを作る
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: '8px',
          backgroundColor: FILE_TYPE_COLORS[fileType],
          borderRadius: '4px'
        },
        // ファイルがある場合は薄い背景色
        backgroundColor: file ? 'rgba(0, 0, 0, 0.03)' : undefined,
      }}
      {...getRootProps()}
      secondaryAction={
        file ? (
          <IconButton
            edge="end"
            aria-label="delete"
            disabled={!onClickDelete}
            onClick={async () => {
              await onClickDelete?.(file!);
            }}
          >
            <X/>
          </IconButton>
        ) : undefined
      }
    >
      <input {...getInputProps()} />
      <ListItemAvatar>
        {isUploading || file?.analyzeStatus === FileAnalyzeStatus.Analyzing ? (
          <CircularProgress size={23}/>
        ) : file ? (
          <Tooltip title="このファイルの解析を実行">
            <span>
            <IconButton
              color={"primary"}
              size={"small"}
              onClick={async () => {
                if (!file) return;
                setIsUploading(true);
                try {
                  await onClickAnalyze?.([file]);
                } finally {
                  setIsUploading(false);
                }
              }}
              disabled={
                file?.analyzeStatus == FileAnalyzeStatus.Analyzed || !onClickAnalyze
              }
            >
              <Brain/>
            </IconButton>
            </span>
          </Tooltip>
        ) : (
          <FileInput
            size={23}
            color={
              // パステルカラーから少し濃い色を生成
              FILE_TYPE_COLORS[fileType] ?
                '#' + FILE_TYPE_COLORS[fileType].replace('#', '').match(/.{2}/g)?.map(
                  c => Math.max(0, parseInt(c, 16) - 40).toString(16).padStart(2, '0')
                ).join('') :
                "#1a73e8"
            }
          />
        )}
      </ListItemAvatar>
      <ListItemText primaryTypographyProps={{noWrap: true}}>
        {file ? (
          <>
            <Typography variant={"caption"} sx={{display: 'flex', alignItems: 'center'}}>
              {title}
            </Typography>
            <span
              style={{
                cursor: "pointer",
                color: "blue",
                textDecoration: "underline",
              }}
              onClick={() => onFileNameClick?.(file)}
            >
              {file.fileName}
            </span>
          </>
        ) : (
          <Box sx={{display: 'flex', alignItems: 'center'}}>
            {title}
          </Box>
        )}
      </ListItemText>
    </ListItem>
  );
}
